// Inventory Repository Implementation

import {InventoryRepository} from './InventoryRepository';
import {InventoryAPIDataSource} from '../api/InventoryAPIDataSource';
import {mapInventoryItemsDTOToModel, mapInventoryItemDTOToModel, mapInventoryFiltersToDTO} from '../mappers/inventory.mapper';
import {InventoryItem, InventoryFilters, AddStockForm} from '../models/inventory.model';
import {InventoryStatistics} from '../models/inventory.model';
import {Pagination} from '../../../Data/types/BaseResponse';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';

import {
  Location,
  TrackedSerial,
  SerialDetails,
  SerialTimelineResponse,
  InventoryMovement,
  ProductInventoryStatus,
  InventoryMovementFilters,
  SerialStatistics
} from '../models/inventory.model';

import {
  mapLocationsDTOToModel,
  mapTrackedSerialsDTOToModel,
  mapSerialDetailsDTOToModel,
  mapSerialTimelineResponseDTOToModel,
  mapInventoryMovementsDTOToModel,
  mapProductInventoryStatusDTOToModel,
  mapInventoryMovementToDTO,
  mapInventoryMovementFiltersToDTO,
  mapInventoryStatisticsDTOToModel,
  mapSerialStatisticsDTOToModel
} from '../mappers/inventory.mapper';

export class InventoryRepositoryImpl implements InventoryRepository {

  // Inventory Items
  async getAllInventoryItems (filters?: InventoryFilters, page = 1, limit = PAGINATION_CONFIG.DEFAULT_PAGE_SIZE): Promise<InventoryItem[]> {
    try {
      const filtersDTO = filters ? mapInventoryFiltersToDTO(filters, page, limit) : undefined;
      const response = await InventoryAPIDataSource.getAllInventoryItems(filtersDTO);
      return mapInventoryItemsDTOToModel(response);
    } catch (error) {
      throw error;
    }
  }

  async getInventoryItemById (id: string): Promise<InventoryItem> {
    try {
      const response = await InventoryAPIDataSource.getInventoryItemById(id);
      return mapInventoryItemDTOToModel(response);
    } catch (error) {
      throw error;
    }
  }

  async addStock (stockData: AddStockForm): Promise<InventoryItem> {
    try {
      const stockDTO = {
        product_id: stockData.productId,
        location_id: stockData.locationId,
        quantity: stockData.quantity,
        serial_numbers: stockData.serialNumbers,
        cost: stockData.cost,
        created_by: stockData.createdBy
      };

      const response = await InventoryAPIDataSource.addStock(stockDTO);
      return mapInventoryItemDTOToModel(response);
    } catch (error) {
      throw error;
    }
  }

  async updateInventoryItem (id: string, updateData: Partial<InventoryItem>): Promise<InventoryItem> {
    try {
      const updateDTO = {
        product_id: updateData.productId,
        location_id: updateData.locationId,
        quantity: updateData.quantity,
        min_stock_level: updateData.minStockLevel,
        max_stock_level: updateData.maxStockLevel,
        status: updateData.status
      };

      const response = await InventoryAPIDataSource.updateInventoryItem(id, updateDTO);
      return mapInventoryItemDTOToModel(response);
    } catch (error) {
      throw error;
    }
  }

  // Locations
  async getAllLocations (): Promise<Location[]> {
    try {
      const response = await InventoryAPIDataSource.getAllLocations();
      return mapLocationsDTOToModel(response);
    } catch (error) {
      throw error;
    }
  }

  // Serial Tracking
  async getSerialDetails (serialNumber: string): Promise<SerialDetails> {
    try {
      const dto = await InventoryAPIDataSource.getSerialDetails(serialNumber);
      return mapSerialDetailsDTOToModel(dto);
    } catch (error) {
      throw error;
    }
  }

  async getSerialTimeline (serialNumber: string, page = 1, limit = 50): Promise<SerialTimelineResponse> {
    try {
      const dto = await InventoryAPIDataSource.getSerialTimeline(serialNumber, page, limit);
      return mapSerialTimelineResponseDTOToModel(dto);
    } catch (error) {
      throw error;
    }
  }

  async getTrackedSerials (filters?: {
    productId?: string;
    locationId?: string;
    status?: string;
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<{ items: TrackedSerial[]; pagination?: Pagination }> {
    try {
      const apiFilters = filters ? {
        product_id: filters.productId,
        location_id: filters.locationId,
        status: filters.status,
        page: filters.page,
        limit: filters.limit,
        search: filters.search
      } : undefined;

      const response = await InventoryAPIDataSource.getTrackedSerials(apiFilters);

      return {
        items: mapTrackedSerialsDTOToModel(response.data),
        pagination: response.pagination ? {
          page: response.pagination.page || 1,
          limit: response.pagination.limit || 20,
          total: response.pagination.total || response.data.length,
          totalPages: response.pagination.totalPages || Math.ceil((response.pagination.total || response.data.length) / (response.pagination.limit || 20))
        } : {
          page: filters?.page || 1,
          limit: filters?.limit || 20,
          total: response.data.length,
          totalPages: 1
        }
      };
    } catch (error) {
      throw error;
    }
  }

  async deleteSerial (id: string): Promise<void> {
    try {
      await InventoryAPIDataSource.deleteSerial(id);
    } catch (error) {
      throw error;
    }
  }

  // Inventory Movements
  async getInventoryMovements (filters?: InventoryMovementFilters, page = 1, limit = PAGINATION_CONFIG.DEFAULT_PAGE_SIZE): Promise<{ items: InventoryMovement[]; pagination?: Pagination }> {
    try {
      const filtersDTO = filters ? mapInventoryMovementFiltersToDTO(filters, page, limit) : undefined;
      const response = await InventoryAPIDataSource.getInventoryMovements(filtersDTO);

      return {
        items: mapInventoryMovementsDTOToModel(response.data),
        pagination: response.pagination ? {
          page: response.pagination.page || page,
          limit: response.pagination.limit || limit,
          total: response.pagination.total || response.data.length,
          totalPages: response.pagination.totalPages || Math.ceil((response.pagination.total || response.data.length) / (response.pagination.limit || limit))
        } : {
          page,
          limit,
          total: response.data.length,
          totalPages: 1
        }
      };
    } catch (error) {
      throw error;
    }
  }

  async createInventoryMovement (movementData: InventoryMovement): Promise<InventoryMovement> {
    try {
      const movementDTO = mapInventoryMovementToDTO(movementData);
      const response = await InventoryAPIDataSource.createInventoryMovement(movementDTO);
      return mapInventoryMovementsDTOToModel([response])[0];
    } catch (error) {
      throw error;
    }
  }

  // Product Inventory Status
  async getProductInventoryStatus (productId?: string): Promise<ProductInventoryStatus[]> {
    try {
      const response = await InventoryAPIDataSource.getProductInventoryStatus(productId);
      return response.map(mapProductInventoryStatusDTOToModel);
    } catch (error) {
      throw error;
    }
  }

  async getInventoryStatistics (): Promise<InventoryStatistics> {
    try {
      const response = await InventoryAPIDataSource.getInventoryStatistics();
      return mapInventoryStatisticsDTOToModel(response);
    } catch (error) {
      throw error;
    }
  }

  async getComprehensiveReport (filters?: any, page = 1, limit = PAGINATION_CONFIG.DEFAULT_PAGE_SIZE): Promise<any> {
    try {
      const response = await InventoryAPIDataSource.getComprehensiveReport(filters);
      // Backend returns an array and a pagination object; adapt to UI shape { items, total_items, ... }
      if (Array.isArray(response)) {
        return {items: response, total_items: response.length};
      }
      if (response && Array.isArray(response.items)) {
        return response;
      }
      // Some backends return { data, pagination }
      if (response && Array.isArray(response.data)) {
        return {
          items: response.data,
          total_items: response.pagination?.total || response.data.length,
          total_quantity: response.pagination?.summary?.total_quantity,
          low_stock_count: response.pagination?.summary?.low_stock_products,
          out_of_stock_count: response.pagination?.summary?.out_of_stock_products,
          page: response.pagination?.page || page,
          limit: response.pagination?.limit || limit
        };
      }
      return {items: [], total_items: 0};
    } catch (error) {
      throw error;
    }
  }

  async getSerialStatistics (): Promise<SerialStatistics> {
    try {
      const response = await InventoryAPIDataSource.getSerialStatistics();
      return mapSerialStatisticsDTOToModel(response);
    } catch (error) {
      throw error;
    }
  }
}
